import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { isUserSuperAdminSelector, CurrentUserSelector } from 'app/selectors/userSelectors';
import type { PersonaDto } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { AccessLevel as AccessLevelType } from '@/api-gen/api.schemas';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';

import type { I18nPersonasNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';
import { getLanguageDisplayName } from '@/app/utils/languageUtils';
import { PersonaSharingChip, isPersonaShared } from '@/features/personas';

interface PersonaCardProps {
  persona: PersonaDto;
  onClick?: (e?: React.MouseEvent) => void;
}

const PersonaCard: React.FC<PersonaCardProps> = ({ persona, onClick }) => {
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCore } = useTranslation(I18nNamespace.Core);
  
  // Get user's access level to this persona
  const getUserAccessLevel = (persona: PersonaDto): AccessLevelType | undefined => {
    if (isSuperAdmin) return AccessLevel.CanAdministrate;
    
    const userCompanyId = currentUser?.companyId;
    if (!userCompanyId) return undefined;
    
    const companyAccess = persona.companies?.find(c => c.companyId === userCompanyId);
    return companyAccess?.access;
  };
  
  const accessLevel = getUserAccessLevel(persona);

  const getAccessIcon = (access?: AccessLevelType) => {
    switch (access) {
      case AccessLevel.CanEdit:
        return <EditIcon fontSize="small" />;
      case AccessLevel.CanAdministrate:
        return <AdminIcon fontSize="small" />;
      default:
        return <ViewIcon fontSize="small" />;
    }
  };

  const getAccessLevelTranslation = (accessLevel?: AccessLevelType) => {
    if (!accessLevel) return '';
    
    switch (accessLevel) {
      case AccessLevel.CanRead:
        return translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canRead));
      case AccessLevel.CanEdit:
        return translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canEdit));
      case AccessLevel.CanAdministrate:
        return translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canAdministrate));
      default:
        return '';
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return 'P';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id?: number) => {
    const colors = [
      '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
      '#c2185b', '#0288d1', '#0097a7', '#00796b'
    ];
    return colors[(id || 0) % colors.length];
  };


  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3
        }
      }}
    >
      <Box 
        onClick={handleCardClick}
        sx={{ 
          flexGrow: 1, 
          cursor: onClick ? 'pointer' : 'default',
          height: '100%',
          '&:hover': onClick ? {
            bgcolor: 'action.hover'
          } : undefined
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: getAvatarColor(persona.id),
                width: 64,
                height: 64,
                mr: 2
              }}
              src={!avatarLoadError && persona.avatarUrl ? getAvatarUrlWithSas(persona.avatarUrl) : undefined}
              alt={persona.name || 'Persona avatar'}
              imgProps={{ onError: () => setAvatarLoadError(true) }}
            >
              {getInitials(persona.name)}
            </Avatar>
            <Box flex={1}>
              <Typography 
                variant="h6" 
                component="h2" 
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.2
                }}
              >
                {persona.name || translate(nameof.full<I18nPersonasNs>((n) => n.card.unnamedPersona))}
              </Typography>
              {persona.languageCode && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getLanguageDisplayName(persona.languageCode, translateCore)} • {persona.ttsProvider}
                </Typography>
              )}
            </Box>
          </Box>

          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '3em',
              mb: 2
            }}
          >
            {persona.description || translate(nameof.full<I18nPersonasNs>((n) => n.card.noDescriptionProvided))}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {persona.gender && (
              <Chip 
                size="small" 
                label={persona.gender}
                variant="outlined"
              />
            )}
            {persona.voiceName && (
              <Chip 
                size="small" 
                label={persona.voiceName}
                variant="outlined"
              />
            )}
          </Stack>
        </CardContent>
      </Box>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
        <Box display="flex" alignItems="center">
          <PersonaSharingChip
            persona={persona}
            tooltipText={translate(nameof.full<I18nPersonasNs>((n) => n.card.sharedWithTooltip))}
          />
          {/* Removed company comparison as these properties don't exist in PersonaDto */}
        </Box>
        
        <Box display="flex" alignItems="center">
          {accessLevel && (
            <Tooltip title={`${translate(nameof.full<I18nPersonasNs>((n) => n.card.yourAccessTooltip))}: ${getAccessLevelTranslation(accessLevel)}`}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getAccessIcon(accessLevel)}
              </Box>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default PersonaCard;